/// <amd-module name="Wit/WorkItemForm/Common/Components/EditWorkItemLinkDialog" />
import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useObservable } from "VSS/Core/Observable";
import { usePageContext, useService } from "VSS/Platform/Layout";
import { FormItem } from "VSSUI/Components/FormItem/FormItem";
import { Dialog } from "VSSUI/Dialog";
import { PanelFooter } from "VSSUI/Panel";
import { TextField } from "VSSUI/TextField";
import { getRelatedWorkNewLinkTypeFilter } from "Wit/Common/Utils/WorkItemUtils/LinkTypeFilter";
import { IWorkItemLinksService } from "Wit/Common/WorkItemLinksService/WorkItemLinksService";
import { IWorkItemLinkTypeEnd } from "Wit/WorkItemDataManager/Contracts";
import { LinkTopologyDiagram } from "Wit/WorkItemForm/Links/Controls/LinkTopologyDiagram";
import { LinkTypeDropdown } from "Wit/WorkItemForm/Links/Controls/LinkTypeDropdown";
import { resolveWorkItemArtifacts } from "Wit/WorkItemForm/Links/WorkItem/resolveWorkItemArtifacts";
import { WorkItemLinkTopologyValidator } from "Wit/WorkItemForm/Links/WorkItem/WorkItemLinkTopologyValidator";
import * as Resources from "Wit/WorkItemForm/Resources";
import { LinkTypeFieldIds, RegisteredLinkTypeNames, ToolIds } from "Wit/WorkItemLinksRegistration/Constants";
import { ILinkIdentifier, IResolvedLinkDisplayData } from "Wit/WorkItemLinksRegistration/Contracts";
import { IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";

export interface IEditWorkItemLinkDialogProps {
    comment: string;
    onEditLink: (editLinkData: { comment: string; removeLink: boolean; linksToAdd: IResolvedLinkDisplayData[] | undefined }) => void;
    onDismiss: () => void;
    linkIdentifier: ILinkIdentifier;
    workItem: IWorkItem;
}

export function EditWorkItemLinkDialog({ comment, onEditLink, onDismiss, linkIdentifier, workItem }: IEditWorkItemLinkDialogProps) {
    const pageContext = usePageContext();
    const linksService = useService<IWorkItemLinksService>("IWorkItemLinksService");
    const [commentObservable, setComment] = useObservable(comment);
    const [witLinkTypeEnd, setWitLinkTypeEnd] = useState<IWorkItemLinkTypeEnd | undefined>();
    const [errors, setErrors] = useState<string[]>([]);
    const [saveDisabled, setSaveDisabled] = useState(false);

    const linkTypeFilter = useMemo(() => getRelatedWorkNewLinkTypeFilter([workItem]), [workItem]);
    const initialLink = useMemo(() => {
        return workItem.getLinks().find(l => l.linkData.FilePath === linkIdentifier?.FilePath && l.linkData.ID === linkIdentifier?.ID);
    }, [workItem, linkIdentifier]);
    const targetWorkItemId = useMemo(() => {
        return linkIdentifier.ID;
    }, [workItem, linkIdentifier]);
    const linkTypeChanged = useMemo(() => {
        return initialLink?.getWitLinkTypeEndId() !== witLinkTypeEnd?.id;
    }, [witLinkTypeEnd, initialLink]);

    useEffect(() => {
        const resolveWitLinkData = async () => {
            const linkTypeEnd = await linksService.getWitLinkTypeEnd(initialLink?.getWitLinkTypeEndId());
            setWitLinkTypeEnd(linkTypeEnd);
        };

        resolveWitLinkData();
    }, [initialLink]);
    useEffect(() => {
        const onAnotherLinkTypeSelected = async () => {
            if (!witLinkTypeEnd || !targetWorkItemId || !initialLink) {
                return;
            }

            if (!linkTypeChanged) {
                setSaveDisabled(false);
                return;
            }

            const errorMessages = await new WorkItemLinkTopologyValidator(pageContext, [workItem], witLinkTypeEnd, [targetWorkItemId], {
                [workItem.id]: [initialLink]
            }).validate();
            setErrors(errorMessages);
            setSaveDisabled(errorMessages.length > 0);
        };

        onAnotherLinkTypeSelected();
    }, [witLinkTypeEnd, workItem, pageContext]);

    const onSaveEditClicked = useCallback(async () => {
        if (!witLinkTypeEnd || !targetWorkItemId || !initialLink) {
            return;
        }
        let linksToAdd: IResolvedLinkDisplayData[] | undefined;
        if (linkTypeChanged) {
            const linkInfos = {
                ID: targetWorkItemId,
                tool: ToolIds.WorkItemTracking,
                FldID: LinkTypeFieldIds.WorkItem,
                LinkType: witLinkTypeEnd.id
            };
            const workItemDisplayData = await resolveWorkItemArtifacts([linkInfos], pageContext, workItem.id);
            const finalizedArtifacts = workItemDisplayData.map(linkDisplayData => ({
                ...linkDisplayData,
                linkIdentifier: { ...linkDisplayData.linkIdentifier, Comment: commentObservable.value },
                registeredLinkType: RegisteredLinkTypeNames.Related,
                witLinkTypeEnd: witLinkTypeEnd.immutableName,
                linkTypeDisplayName: witLinkTypeEnd.name
            }));
            linksToAdd = finalizedArtifacts;
        }
        onEditLink({
            comment: commentObservable.value,
            removeLink: linkTypeChanged,
            linksToAdd
        });
    }, [commentObservable, onEditLink, witLinkTypeEnd, targetWorkItemId, initialLink, linkTypeChanged]);

    return (
        <Dialog className="add-link-dialog" titleProps={{ text: Resources.EditLink }} onDismiss={onDismiss}>
            <div className="link-type-field margin-bottom-8">
                <FormItem label={Resources.LinkTypeLabel}>
                    <LinkTypeDropdown
                        ariaLabel={Resources.LinkTypeLabel}
                        linkTypeFilter={linkTypeFilter}
                        defaultLinkTypeId={witLinkTypeEnd?.immutableName}
                        onLinkTypeChanged={lt => {
                            setWitLinkTypeEnd(lt.workItemLinkTypeEnd);
                        }}
                    />
                </FormItem>
            </div>

            <div className="link-type-specific-content margin-bottom-8">
                <div className="work-item-link-form">
                    <LinkTopologyDiagram workItemLinkTypeEnd={witLinkTypeEnd?.immutableName} />

                    {errors.length === 0 ? null : (
                        <div className="work-item-link-form-errors flex-column padding-vertical-4 padding-horizontal-8 margin-top-8">
                            {errors.map((error, i) => (
                                <div className="padding-vertical-4" key={i}>
                                    {error}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="comment-field">
                <FormItem label={Resources.LinkCommentLabel}>
                    <TextField
                        placeholder={Resources.LinkCommentPlaceholder}
                        value={commentObservable}
                        onChange={(e, val) => {
                            setComment(val);
                        }}
                    />
                </FormItem>
            </div>

            <PanelFooter
                buttonProps={[
                    {
                        primary: true,
                        text: Resources.Save,
                        onClick: onSaveEditClicked,
                        disabled: saveDisabled
                    },
                    { text: Resources.Cancel, onClick: onDismiss }
                ]}
            />
        </Dialog>
    );
}
